import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  subDays,
  subMonths,
} from "date-fns";

export type DateRangePreset =
  | "today"
  | "this_week"
  | "this_month"
  | "last_30_days"
  | "last_month"
  | "this_quarter"
  | "ytd"
  | "all_time"
  | "custom";

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

/**
 * Borne une selection de calendrier sur des journees entieres.
 *
 * react-day-picker rend MINUIT pour `from` et pour `to`. Passees telles quelles
 * a une requete, un clic sur un seul jour donne `date >= 00:00 AND date <= 00:00`
 * — une fenetre de largeur nulle qui n'attrape aucun trade intraday. Les presets,
 * eux, bornent deja leurs journees : seule la selection manuelle doit passer ici.
 *
 * `to` reste null tant que la plage est en cours de selection (premier clic),
 * sinon la selection se refermerait sur son premier jour.
 */
export function normalizeCustomRange(
  range: { from?: Date | null; to?: Date | null } | undefined,
): DateRange {
  return {
    from: range?.from ? startOfDay(range.from) : null,
    to: range?.to ? endOfDay(range.to) : null,
  };
}

export function presetToDateRange(preset: DateRangePreset): DateRange {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "this_week":
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
    case "this_month":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "last_30_days":
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case "this_quarter":
      return { from: startOfQuarter(now), to: endOfQuarter(now) };
    case "ytd":
      return { from: startOfYear(now), to: endOfDay(now) };
    case "all_time":
    case "custom":
    default:
      return { from: null, to: null };
  }
}

